import { useCallback, useEffect, useRef } from "react";
import { Redirect, useHistory } from "react-router";
import { useSelector } from "react-redux";
import {
  setDisableSystemCheck,
  setIsSystemCheckCompleted,
} from "modules/systemCheck/redux/systemCheckSlice";
import { PageLoader } from "@remo-co/ui-core/src/components/PageLoader";
import { useI18n } from "i18n";
import { Errors, trackError } from "modules/monitoring";
import { useAppDispatch } from "store/hooks";
import {
  askDevicesPermissions,
  selectAvailableMediaStreamConstraints,
} from "modules/deviceInfo";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import { stringify } from "query-string";
import { hasAutoJoin } from "modules/queryParams/queryParamsHelper";
import TrialExpired from "./TrialExpired";
import {
  EventPrechecks,
  PrecheckStatus,
  useEventPreChecks,
} from "../prechecks";
import { selectCurrentEvent, selectHasJoinedEvent } from "../selectors";

export const AUTO_JOIN_PARAM = "autojoin";
const useIsMounted = () => {
  const isMountedRef = useRef(false);
  const isMounted = useCallback(() => isMountedRef.current, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMounted;
};

interface PrecheckProps {
  children: React.ReactElement;
}
const EventPreChecks = ({ children }: PrecheckProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const currentEvent = useSelector(selectCurrentEvent);
  const params = useQueryParams();
  const isAutoJoin = hasAutoJoin(params);
  const isMounted = useIsMounted();
  const { t } = useI18n();
  const availableMediaStreamConstraints = useSelector(
    selectAvailableMediaStreamConstraints,
  );

  const hasJoinedEvent = useSelector(selectHasJoinedEvent);

  const eventPrechecks = useEventPreChecks();

  useEffect(() => {
    // Check to remove autojoin query param. See RC-3212 for reference.
    if (isMounted() && isAutoJoin) {
      dispatch(setIsSystemCheckCompleted(false));
      dispatch(setDisableSystemCheck(true));
      const { autojoin: _autojoin, ...restParams } = params;
      history.replace({
        search: stringify(restParams),
      });
    }
  }, [isAutoJoin, isMounted, history, dispatch, params]);

  if (eventPrechecks.status === PrecheckStatus.PENDING) {
    return <PageLoader message={t("event:message.wait")} />;
  }

  if (eventPrechecks.status === PrecheckStatus.PASS || !hasJoinedEvent) {
    return children;
  }

  switch (eventPrechecks.name) {
    case EventPrechecks.TRIAL_EXPIRED: {
      return <TrialExpired />;
    }

    case EventPrechecks.ONBOARDING: {
      return <Redirect to={`/onboarding?event=${currentEvent?.code}`} />;
    }

    case EventPrechecks.LOBBY:
    case EventPrechecks.MIC_CAM_CHECK: {
      dispatch(askDevicesPermissions(availableMediaStreamConstraints));
      return <Redirect to={`/mic-cam-check?event=${currentEvent?.code}`} />;
    }

    default: {
      // we shouldn't reach this case unless we encounter an unhandled check
      trackError(eventPrechecks.name, { label: Errors.UNHANDLED_EVENT_CHECK });
      history.push(`/e/${currentEvent?.code}/register`);
    }
  }
  return null;
};

export default EventPreChecks;
